import type { FiltersState, HistoryStatus } from '../../eventHelpers'
import type { EmailKind } from '../types/events.types'

export const HISTORY_STORAGE_KEY = 'landlord/events/history/v1'
export const PAGE_SIZE = 21
export const DEFAULT_DELAY_MS = 5 * 60_000

export const DEFAULT_TRIAL_DAYS = 30

export const DEFAULT_TRIAL_TITLE = 'A 1-month Premium trial is yours 🎁'
export const DEFAULT_TRIAL_BODY =
    "Hi {{firstName}},\n\nWe love how active you've been on BSO Club lately. As a thank-you, we're unlocking a free 1-month Premium trial for you — starting today. Enjoy priority placement, VIP areas, and advanced tools for the next 30 days.\n\nTap to explore your Premium features now.\n\n— BSO Club Team"

export const DEFAULT_TRIAL_DEEP_LINK = '/agent'

// Email preview templates for unit_update (readable preview for admin)
export const DEFAULT_UNIT_UPDATE_TITLE = 'Update on your unit: {{unitAddress}}'
export const DEFAULT_UNIT_UPDATE_BODY =
    'Hi {{firstName}},\n\n' +
    'There is an update regarding your unit: {{unitAddress}}.\n\n' +
    '{{adminMessage}}\n\n' +
    'Tap the button below to view the unit details.\n\n' +
    '— BSO Club Team'

export const DEFAULT_DROP_PRICE_TITLE = 'Price update for {{unitAddress}}'
export const DEFAULT_DROP_PRICE_BODY =
    'Dear {{fullName}},\n\n' +
    'Please be advised that the landlord has just updated the listing price for {{unitAddress}}.\n\n' +
    'Old Price : {{unitPrice}}\n' +
    'New Authorized Price: {{unitDiscountPrice}}\n\n' +
    'Because the price has changed, your current Marketing Authorization Form is now outdated. To ensure your advertisements remain compliant with RERA regulations and avoid potential fines, you must download the updated form and refresh your listings immediately.\n\n' +
    'Click the button below to download your updated document from the BSO Club app:\n\n' +
    'Open BSO Club app\n\n' +
    'Required Action:\n' +
    '- Update Portals: Update the price on all external portals (Property Finder, Bayut, etc.) to match the new authorized amount.\n' +
    '- Replace Files: Delete the previous authorization version and replace it with the updated document linked above.\n\n' +
    'Advertising a unit at an unauthorized price is a significant compliance risk in Dubai. Thank you for your immediate attention to this update.\n\n' +
    'Best regards,\n' +
    'BSO Club Compliance Team'

export const DEFAULT_ACCOUNT_BLOCKED_TITLE = 'Important notice regarding your BSO Club account'
export const DEFAULT_ACCOUNT_BLOCKED_BODY =
    'Hi {{fullName}},\n\n' +
    'We are contacting you to inform you that your BSO Club account has been suspended due to a formal violation of our Terms and Conditions.\n\n' +
    '{{adminMessage}}\n\n' +
    'As a result of this breach, your account has been restricted by our team. We take the integrity of our platform seriously, and all users are expected to adhere to the agreed-upon standards. If you believe this action was made in error or you would like to clarify the situation, please review our Terms and contact support using the details below.\n\n' +
    'Email: {{supportEmail}}\n' +
    'Phone: {{supportPhone}}'

export const DEFAULT_ACCOUNT_BLOCKED_TERMS_URL = ''
export const DEFAULT_ACCOUNT_BLOCKED_SUPPORT_EMAIL = 'brokers@bso.ae'
export const DEFAULT_ACCOUNT_BLOCKED_SUPPORT_PHONE = '+971526477228'

export const DEFAULT_ACCOUNT_UNBLOCKED_TITLE = 'Your BSO Club account has been reinstated'
export const DEFAULT_ACCOUNT_UNBLOCKED_BODY =
    'Hi {{fullName}},\n\n' +
    'We are pleased to inform you that your BSO Club account has been successfully reinstated.\n\n' +
    'Your account access has been fully restored, and you may resume using our services immediately. We appreciate your patience and cooperation throughout the review process. To ensure a seamless experience moving forward, we kindly remind you to remain in compliance with our Terms and Conditions.\n\n' +
    'If you have any questions or require further assistance, please do not hesitate to contact us.\n\n' +
    'Email: {{supportEmail}}\n' +
    'Phone: {{supportPhone}}'

export const RELEASE_V2_LIVE_PREVIEW_SUBJECT = '🚀 The All-New BSO Club is LIVE'
export const RELEASE_V2_LIVE_PREVIEW_BODY =
    'Dear Valued {{firstName}},\n\n' +
    'The wait is officially over! We\'ve pulled back the curtain on the new and improved BSO Club, and it\'s packed with power-ups designed to help you close more deals with less effort.\n\n' +
    'Our goal was simple: make your workflow faster, your listings shinier, and your life easier.\n\n' +
    'What\'s New? (And Why You\'ll Love It)\n\n' +
    '🔓Unlimited Freedom: Subscriptions are now simplified! No more being tied to specific areas, choose a plan that fits how you work and scale instantly.\n\n' +
    '🏗️Monthly Bricks Refresh: Your "Bricks" now refresh every month based on your plan. Enjoy predictable rewards and easier planning for your business growth.\n\n' +
    '✨Pro-Level Unit Previews: Impress your clients with a stunning new layout. Share unit details (including owner info) in a cleaner, more professional format that screams "Top Agent."\n\n' +
    '🔥Boost Your Visibility: We\'ve made unit promotion more flexible. List and promote your units more frequently to get your properties in front of the right eyes, faster.\n\n' +
    '💬Lightning-Fast Chat: Stop scrolling, start talking. Our enhanced chat filters let you find conversations in seconds so you never miss a lead.\n\n' +
    '🎨Sleek New Interface: Experience a modern, intuitive design that makes navigating the app feel like a breeze.\n\n' +
    'Ready to Level Up?\n' +
    'Your account, data, and history are all right where you left them. All you need to do is log in, accept the new terms, and start exploring the future of real estate management.\n\n' +
    'The new era of BSO Club is just one click away.\n'

export const standardEmails = ["a.memetov@bso.ae", "asif.sssprop@gmail.com", "khalifa.alsuwaidi.realestate@gmail.com", "faiza@nexhome.ae", "aliev198@inbox.ru", "aabdullahimran73@gmail.com", "mansi@lindas.ae", "amanda.c@famproperties.com", "nashsaviorodrigues@gmail.com", "alinawaz29@gmail.com", "ramzis92@gmail.com", "akhan@igguae.com", "kristhelronedelacruz@gmail.com", "farwaminhas1234@gmail.com", "umranijunaid@gmail.com", "irfanaliumrani6821@gmail.com"]

export const EMAIL_TEMPLATE_IDS: Record<EmailKind, string> = {
    trial: 'd-5ba436aa341949acba56cdf00e1c098c',
    unit_update: 'd-f3d83933b128403ea0858cc4ecb2e71f',
    drop_price: 'd-50340e66e9344cd7881ab6d46dc30f99',
    universal: 'd-aa927df9bb884ead9b3411d41600dc4a',
    account_blocked: 'd-939e621b8d134ee1bf1da8fdab55c812',
    account_unblocked: 'd-8d13423f9c5145458211eae4a0631b5c',
    releaseV2Warm: 'd-d511e631fada47d6a0be4310f7e78b88',
    basicReleaseV2: 'd-3cf51651147549a2a89a02b99ebd61cb',
    basicReleaseForStandardUsersV2: 'd-395622af1fe7429782ef57c898f8f5c5'
}

export const statusBadgeVariant: Record<HistoryStatus, string> = {
    pending: 'warning',
    sent: 'success',
    failed: 'danger',
}

export const statusLabel: Record<HistoryStatus, string> = {
    pending: 'Pending',
    sent: 'Sent',
    failed: 'Failed',
}

export const defaultFilters: FiltersState = {
    id: '',
    name: '',
    email: '',
    category: 'all',
    verified: 'all',
    blocked: 'all',
    hasUnitsOnly: false,
    pushableOnly: true,
}
